import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';

interface BackendNotification {
  id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
}

export const useSystemNotifications = () => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // Fetch notifications from backend
  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      return response.data.notifications as BackendNotification[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get unread count
  const { data: unreadCount } = useQuery({
    queryKey: ['notificationUnreadCount'],
    queryFn: async () => {
      const response = await api.get('/notifications/unread-count');
      return response.data.unread_count as number;
    },
    refetchInterval: 30000,
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await api.put(`/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put('/notifications/read-all');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
    }
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
      showNotification({
        type: 'success',
        title: 'Powiadomienie usunięte',
        message: 'Powiadomienie zostało pomyślnie usunięte.'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd usuwania',
        message: error.response?.data?.message || 'Nie udało się usunąć powiadomienia.'
      });
    }
  });

  // Delete all read notifications mutation
  const deleteAllReadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/notifications/read');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
      showNotification({
        type: 'success',
        title: 'Powiadomienia usunięte',
        message: 'Wszystkie przeczytane powiadomienia zostały usunięte.'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd usuwania',
        message: error.response?.data?.message || 'Nie udało się usunąć powiadomień.'
      });
    }
  });

  // Show unread notifications as UI notifications
  useEffect(() => {
    if (notifications) {
      const unreadNotifications = notifications.filter(n => !n.is_read);
      const lastShownIds = new Set(
        JSON.parse(localStorage.getItem('shownNotificationIds') || '[]')
      );

      unreadNotifications.forEach(notification => {
        if (!lastShownIds.has(notification.id)) {
          showNotification({
            type: notification.type as any,
            title: notification.title,
            message: notification.message,
            duration: 7000,
            actions: [{
              label: 'Mark as read',
              onClick: () => markAsReadMutation.mutate(notification.id)
            }]
          });
          lastShownIds.add(notification.id);
        }
      });

      localStorage.setItem('shownNotificationIds', JSON.stringify(Array.from(lastShownIds)));
    }
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    deleteAllRead: deleteAllReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeletingNotification: deleteNotificationMutation.isPending,
    isDeletingAllRead: deleteAllReadMutation.isPending
  };
};